// Chat + command console (UI-5). A persistent bottom-left log plus a transient
// input opened with T (chat) or / (command). Enter runs a command via
// game.runCommand or echoes chat; Tab completes; Esc cancels. Movement is
// suspended while open (input.ts checks game.chatOpen). Pointer lock is kept —
// releasing it would trip the pause menu — so keydowns are stopped from bubbling.
import { h } from '../h';
import { completeCommand } from '../../../sim/game/commands/core';
import type { Game } from '../../boot';

const MAX_LOG = 8;

export function installChat(game: Game): void {
  const history: string[] = [];
  const log = h('div', {
    style: {
      position: 'fixed', left: '10px', bottom: '10px', maxWidth: '48vw',
      font: '13px/1.5 ui-monospace, monospace', color: '#e6ebf5',
      textShadow: '0 1px 2px #000', pointerEvents: 'none', zIndex: '40', whiteSpace: 'pre-wrap',
    },
  });
  document.body.append(log);

  const render = (): void => {
    log.textContent = history.slice(-MAX_LOG).join('\n');
  };
  game.print = (msg: string): void => {
    history.push(msg);
    render();
  };

  let input: HTMLInputElement | null = null;
  const close = (): void => {
    game.chatOpen = false;
    input?.remove();
    input = null;
  };

  const open = (initial: string): void => {
    if (game.chatOpen) return;
    game.chatOpen = true;
    input = h('input', {
      class: 'bb-input',
      value: initial,
      spellcheck: false,
      style: { position: 'fixed', left: '10px', bottom: '10px', width: '48vw', zIndex: '41' },
    }) as HTMLInputElement;
    document.body.append(input);
    input.focus();
    input.setSelectionRange(initial.length, initial.length);

    input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // keep movement/pause handlers from seeing chat typing
      if (e.code === 'Enter') {
        const text = input!.value.trim();
        if (text.startsWith('/')) {
          const res = game.runCommand(text);
          game.print(res.ok ? res.message : `§ ${res.message}`);
        } else if (text && game.net) {
          // Multiplayer: send to the server; it echoes a Chat back to everyone
          // (including us), so we don't local-print here (M27).
          game.net.sendChat(text);
        } else if (text) {
          game.print(`<you> ${text}`);
        }
        close();
      } else if (e.code === 'Escape') {
        close();
      } else if (e.code === 'Tab') {
        e.preventDefault();
        const suggestions = completeCommand(game.cmdContext, input!.value);
        if (suggestions.length === 1) {
          const toks = input!.value.replace(/^\//, '').split(' ');
          toks[toks.length - 1] = suggestions[0]!;
          input!.value = `/${toks.join(' ')}`;
        } else if (suggestions.length > 1) {
          game.print(suggestions.join('  '));
        }
      }
    });
  };

  document.addEventListener('keydown', (e) => {
    if (game.chatOpen || !game.locked) return;
    if (e.code === 'KeyT') { e.preventDefault(); open(''); }
    else if (e.code === 'Slash') { e.preventDefault(); open('/'); }
  });
}
